/* eslint-disable @reatom/unit-naming-rule */

import { action, atom } from '@reatom/framework'

import { reatomNumericInput } from '@root/shared/lib'

import type { Atom } from '@reatom/framework'
import type { WalletModel } from '@root/entities/wallet'
import type BigNumber from 'bignumber.js'

type InputSource = 'amount' | 'percentage' | 'auto'

export const reatomDistributeTarget = ({
  wallet,
  allocatedAmountAtom,
}: {
  wallet: WalletModel
  allocatedAmountAtom: Atom<BigNumber>
}) => {
  const inputSourceAtom = atom<InputSource>('auto')

  const amountAtom = reatomNumericInput({})
  const percentageAtom = reatomNumericInput({ initialAmount: 0 })

  const setRawAmountAction = action((ctx, rawAmount: string) => {
    inputSourceAtom(ctx, 'amount')
    amountAtom.setRaw(ctx, rawAmount)
  })

  const setPercentageAction = action((ctx, rawPercents: string) => {
    inputSourceAtom(ctx, 'percentage')
    percentageAtom.setRaw(ctx, rawPercents.toString())
  })

  const unlockAction = action((ctx) => {
    inputSourceAtom(ctx, 'auto')
  })

  const resolvedAmountAtom = atom((ctx) => {
    const source = ctx.spy(inputSourceAtom)
    if (source === 'percentage') {
      const allocatedAmount = ctx.spy(allocatedAmountAtom)
      const percentage = ctx.spy(percentageAtom).dividedBy(100)
      return allocatedAmount.multipliedBy(percentage)
    }
    return ctx.spy(amountAtom)
  })

  const resolvedPercentageAtom = atom((ctx) => {
    const source = ctx.spy(inputSourceAtom)
    if (source === 'amount') {
      const allocatedAmount = ctx.spy(allocatedAmountAtom)
      const amount = ctx.spy(amountAtom)
      return amount.dividedBy(allocatedAmount).multipliedBy(100)
    }
    return ctx.spy(percentageAtom)
  })

  return {
    wallet,
    amountAtom,
    setRawAmountAction,
    percentageAtom,
    setPercentageAction,
    inputSource: inputSourceAtom,
    unlock: unlockAction,
    resolvedAmount: resolvedAmountAtom,
    resolvedPercentage: resolvedPercentageAtom,
  }
}
export type DistributeTargetModel = ReturnType<typeof reatomDistributeTarget>
